import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import TableFilterDynamique, {
  FilterField,
} from "components/Common/TableFilter/TableFilterDynamique";

import { useTeacherExamSessions } from "../hooks/useTeacherExamSessions";
import TeacherExamSessionList from "../components/TeacherExamSessionList";
import { useSelector } from "react-redux";
import useTeacher from "components/Peoples/Teacher/hooks/useTeacher";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

const TeacherExamSessionPage: React.FC = () => {
  const { t } = useTranslation();

  const teacherId = useSelector(
    (state: any) => state.authSlice.user?.roleId
  );

  const {
    examSessions,
    isLoading,
    fetchExamSessions,
    pagination,
  } = useTeacherExamSessions(teacherId);

  const { classes, fetchClassesTeacher } = useTeacher();

  const [filters, setFilters] = useState<{ [key: string]: any }>({});

  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "my_exam_sessions", active: true },
  ];

  const subjectOptions = useMemo(() => {
    if (!classes?.length) return [];

    const allSubjects = classes.flatMap((cls: any) =>
      Array.isArray(cls.subjects) ? cls.subjects : []
    );

    const uniqueSubjectsMap = new Map<string, any>();

    allSubjects.forEach((subject: any) => {
      if (!uniqueSubjectsMap.has(subject.subjectId)) {
        uniqueSubjectsMap.set(subject.subjectId, {
          value: subject.subjectId,
          label:
            getSubjectLabelFromCode(subject.subjectCode) || subject.subjectName,
          code: subject.subjectCode,
        });
      }
    });

    return Array.from(uniqueSubjectsMap.values());
  }, [classes]);

  const filterFields: FilterField[] = useMemo(
    () => [
      { key: "name", label: t("exam_session_name"), type: "text" },
      {
        key: "subjectId",
        label: t("subject"),
        type: "select",
        options: subjectOptions ?? [],
      },
      {
        key: "semester",
        label: t("semester"),
        type: "select",
        options: [
          { label: t("semester_1"), value: "S1" },
          { label: t("semester_2"), value: "S2" },
        ],
      },
      { key: "startDate", label: t("start_date"), type: "date" },
    ],
    [t, subjectOptions]
  );

  const handleFilter = (newFilters: { [key: string]: any }) => {
    const { startDate, ...rest } = newFilters || {};

    const normalizedStartDate =
      startDate && startDate.format
        ? startDate.format("YYYY-MM-DD")
        : startDate || null;

    const merged = {
      ...filters,
      ...rest,
      startDate: normalizedStartDate,
    };

    setFilters(merged);

    fetchExamSessions({
      ...merged,
      page: 1,
      limit: 10,
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    fetchExamSessions({
      page: 1,
      limit: 10,
    });
  };

  useEffect(() => {
    fetchClassesTeacher?.(teacherId);
    fetchExamSessions({
      page: 1,
      limit: 10,
    });
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("my_exam_sessions")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilter}
            onReset={handleResetFilters}
          />
        </div>

        <TeacherExamSessionList
          examSessions={examSessions}
          loading={isLoading}
          dataPaginate={{
            current: pagination?.page,
            pageSize: pagination?.limit,
            total: pagination?.total,
          }}
          onChange={(pg: any) => {
            fetchExamSessions({
              ...filters,
              page: pg.current!,
              limit: pg.pageSize!,
            });
          }}
        />
      </div>
    </div>
  );
};

export default TeacherExamSessionPage;
